// Case Study data. single source of truth for /case-studies/{slug} pages.
//
// To add a new case study, copy the shape below and fill in the fields the
// user provides. Keys marked OPTIONAL can be omitted; the template handles
// gracefully. Slugs must be kebab-case and unique.
//
// Field reference:
//   slug              kebab-case URL segment, e.g. "rinaldi-residence-magill"
//   testimonialAuthor must EXACTLY match the testimonial.author in ResultsPage.jsx
//                     so the "Read their story" button links correctly.
//   metaTitle         <title> tag (~60 chars max)
//   metaDescription   <meta description> (~155 chars max)
//   heroImage         primary hero photo URL (16:9 ideal)
//   shortQuote        one-line callout (used in cards + hero)
//   client            { name, location, suburb, councilArea, projectType }
//   project           { type, region, builderPartner OPTIONAL, blockType OPTIONAL,
//                       size OPTIONAL, bedrooms OPTIONAL, bathrooms OPTIONAL,
//                       livingZones OPTIONAL, garage OPTIONAL,
//                       startedAt ISO, completedAt ISO OPTIONAL,
//                       durationMonths OPTIONAL }
//   challenge         the problem the family came to BuildPilot with (paragraph)
//   process           array of {title, body} step-by-step
//   solution          how it was solved + outcome (paragraph or two)
//   highlights        array of strings. bullet wins
//   beforeAfter       OPTIONAL { before: imageUrl, after: imageUrl, caption }
//   gallery           OPTIONAL array of imageUrls
//   pullQuote         OPTIONAL longer-form client quote (different from shortQuote)
//   relatedGuides     array of {title, href}. internal links (5+ recommended for SEO)
//   keywords          array of search terms this case study should rank for

export const caseStudyData = {
  "rinaldi-residence-magill": {
    slug: "rinaldi-residence-magill",
    testimonialAuthor: "Rinaldi Residence",
    metaTitle: "Rinaldi Residence: Magill Custom Build Case Study | BuildPilot",
    metaDescription:
      "How BuildPilot helped first-time builders in Magill, SA work through site review, builder type selection and design direction for a fully custom home that suits the area.",
    heroImage: "/images/case-studies/rinaldi-residence-magill.jpg",
    cardImage: "/images/case-studies/rinaldi-residence-magill-card.jpg",
    shortQuote:
      "A fully custom home in Magill, designed to suit the family and the surrounding character of the area.",
    publishedAt: "2026-02-25",
    updatedAt: "2026-02-25",
    hideCta: false,
    client: {
      name: "Rinaldi Residence",
      location: "Magill, SA 5072",
      suburb: "Magill",
      councilArea: "City of Burnside",
      familySituation: "First-time builders",
    },
    project: {
      type: "Custom new build",
      region: "Eastern Adelaide",
      builderPartner: "Custom builder",
      blockType: "Existing block, established Magill",
      sizeSqm: null,
      bedrooms: null,
      bathrooms: null,
      garage: null,
      startedAt: null,
      completedAt: null,
      durationMonths: "Approximately 8",
      timeToCommence: "Approximately 8 to 9 months",
    },
    challenge:
      "The Rinaldi family approached BuildPilot after securing a block in Magill. They had a clear intention to build a home that suited the area but were unsure how to move forward. They had explored a few builder options and seen designs they liked, yet felt restricted in what was being offered. As first-time builders, it was difficult to assess whether the advice they were receiving was right for their situation. There was also uncertainty around the block itself, particularly whether there were any constraints that could affect the design and build.",
    sectionLabels: {
      challenge: "The Situation",
      process: "Approach",
      solution: "Outcome",
      highlights: "Key Takeaways",
    },
    challengeSubpoints: [
      "Gaining a clear understanding of the site and any limitations",
      "Identifying the most suitable type of builder for the project",
      "Ensuring the design direction aligned with both the client's vision and the surrounding area",
    ],
    process: [
      {
        title: "Detailed land review",
        body:
          "The process began with a detailed review of the land. This identified constraints that were not immediately obvious and allowed for more informed planning before the design phase began.",
      },
      {
        title: "Builder category education",
        body:
          "Rather than approaching builders at random, the different builder categories were broken down and explained in the context of the project. This made it easier to determine that a custom builder was the most appropriate path.",
      },
      {
        title: "Structured design discussions",
        body:
          "Design conversations were structured around what could realistically be achieved on the site while still meeting the family's expectations. This kept the design direction practical and aligned with the character of the area.",
      },
    ],
    solution:
      "A fully custom home that suited both the client's requirements and the surrounding homes in Magill. The site was understood early, the right builder type was selected upfront, and the design was anchored to both the family's brief and the area's character.",
    highlights: [
      "Builder type matched to project: custom builder",
      "Time to commence construction: approximately 8 to 9 months",
      "Construction duration: approximately 8 months",
      "Design aligned to both the family's brief and the surrounding Magill streetscape",
      "Early clarity on the block prevented design changes later in the build",
    ],
    keyTakeaways:
      "This project highlights the importance of understanding both the land and the type of builder required before progressing too far into the building process. Early clarity in these areas allowed for better decision-making and a smoother overall experience.",
    beforeAfter: null,
    gallery: [],
    pullQuote: null,
    relatedGuides: [
      { title: "Building in the City of Burnside", href: "/building-in/burnside" },
      { title: "Home Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "First Home Buyer Guide", href: "/first-home-buyer-adelaide" },
      { title: "Display Homes Adelaide", href: "/display-homes-adelaide" },
      { title: "Single Storey Builders in Eastern Adelaide", href: "/single-storey-builders-eastern-adelaide" },
    ],
    keywords: [
      "magill custom home builder",
      "building in magill sa",
      "city of burnside new build",
      "first time builder magill",
      "custom home magill case study",
      "buildpilot client magill",
    ],
  },

  "sterai-residence-ingle-farm": {
    slug: "sterai-residence-ingle-farm",
    testimonialAuthor: "Sterai Residence",
    metaTitle: "Sterai Residence: Ingle Farm KDR on a Triangular Block | BuildPilot",
    metaDescription:
      "How BuildPilot helped a busy family knockdown rebuild on a triangular block in Ingle Farm, SA. Hybrid major builder + custom design, 8 month build, oversight throughout.",
    heroImage: "/images/case-studies/sterai-residence-ingle-farm.jpg",
    cardImage: "/images/case-studies/sterai-residence-ingle-farm-card.jpg",
    shortQuote:
      "A practical, well-planned knockdown rebuild on a tricky triangular block in Ingle Farm.",
    publishedAt: "2026-02-26",
    updatedAt: "2026-02-26",
    hideCta: false,
    client: {
      name: "Sterai Residence",
      location: "Ingle Farm, SA 5098",
      suburb: "Ingle Farm",
      councilArea: "City of Salisbury",
      familySituation: "Busy family balancing business and interstate commitments",
    },
    project: {
      type: "Knockdown rebuild",
      region: "Northern Adelaide",
      builderPartner: "Major builder with custom design elements",
      blockType: "Triangular block, existing dwelling demolished",
      sizeSqm: null,
      bedrooms: null,
      bathrooms: null,
      garage: null,
      startedAt: null,
      completedAt: null,
      durationMonths: "Approximately 8",
      timeToCommence: "Approximately 12 months",
    },
    challenge:
      "The Sterai family approached BuildPilot while managing a busy schedule across their business and interstate commitments. They were living in an existing home in Ingle Farm and were considering a knockdown rebuild. After reviewing the home in person, it became clear that the existing layout and structure no longer suited what they wanted long term. Their goal was to rebuild on the same site, keep the project economical, and still achieve a home that had the feel of a well-designed, quality build. They were not focused on high-end finishes; they wanted to maximise the potential of the block, get the layout right, and create something practical and well thought out. The challenge was that the site itself was not straightforward.",
    sectionLabels: {
      challenge: "The Situation",
      process: "Approach",
      solution: "Outcome",
      highlights: "Key Takeaways",
    },
    challengeSubpoints: [
      "A standard design would not fully suit the triangular block",
      "The layout needed careful planning to avoid wasted space",
      "Orientation and flow had to be considered more closely than a typical site",
      "The clients had limited capacity to be heavily involved day to day",
    ],
    process: [
      {
        title: "Triangular block analysis",
        body:
          "The process started with understanding how to best utilise the triangular block. Rather than forcing a standard design onto the site, time was spent exploring layout options that would maximise usable space and improve overall flow.",
      },
      {
        title: "Hybrid design approach",
        body:
          "A hybrid approach was developed: part of the home was designed with more custom input to suit the block, while other areas were aligned with standard inclusions from a major builder. This kept the project cost-effective while still achieving a tailored outcome where it mattered most.",
      },
      {
        title: "Builder selection aligned to approach",
        body:
          "A major builder was chosen, with approval to incorporate custom elements into the design. This unlocked the cost benefits of a volume-style build with the design flexibility a non-standard block needed.",
      },
      {
        title: "Ongoing oversight",
        body:
          "Throughout the process, ongoing oversight ensured the builder remained aligned with expectations, progress was consistent, and any concerns were addressed early. This gave the clients reassurance while they focused on their own commitments.",
      },
    ],
    solution:
      "The final result was a well-executed knockdown rebuild that made strong use of a challenging triangular site in Ingle Farm. The hybrid major-builder + custom-design approach delivered the layout the family needed without overcomplicating the build, and the active oversight throughout meant the clients could stay focused on their business and interstate commitments while the project moved forward predictably.",
    highlights: [
      "Builder type matched to project: major builder with custom design elements",
      "Time to commence construction: approximately 12 months",
      "Construction duration: approximately 8 months",
      "Triangular block utilised efficiently through tailored layout planning",
      "Cost-effective hybrid design. custom only where the block demanded it",
      "Ongoing oversight kept the build on track without daily client involvement",
    ],
    keyTakeaways:
      "This project demonstrates that even with a more complex block, a practical and well-planned approach can deliver a strong outcome without overcomplicating the build. By focusing on layout and site utilisation first, and applying custom design only where needed, the project remained both efficient and effective.",
    beforeAfter: null,
    gallery: [],
    pullQuote: null,
    relatedGuides: [
      { title: "Building in the City of Salisbury", href: "/building-in/salisbury" },
      { title: "Knockdown Rebuild Adelaide", href: "/knockdown-rebuild-adelaide" },
      { title: "Home Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "Single Storey Builders in Northern Adelaide", href: "/single-storey-builders-northern-adelaide" },
      { title: "Family Home Builders in Northern Adelaide", href: "/family-home-builders-northern-adelaide" },
      { title: "Cost to Build a House in Adelaide", href: "/building-costs-adelaide-2026" },
    ],
    keywords: [
      "knockdown rebuild ingle farm",
      "ingle farm home builder",
      "city of salisbury kdr",
      "triangular block home design adelaide",
      "northern adelaide knockdown rebuild",
      "buildpilot client ingle farm",
    ],
  },

  "federuzzi-development-ingle-farm": {
    slug: "federuzzi-development-ingle-farm",
    testimonialAuthor: "Federuzzi Development",
    metaTitle: "Federuzzi Development: Ingle Farm Subdivision Case Study | BuildPilot",
    metaDescription:
      "How BuildPilot helped Federuzzi Development squeeze two double-garage homes onto an awkward subdivision block in Ingle Farm, SA. One-on-one turnkey project, frontage stretched, equity-released for the next investment.",
    heroImage: "/images/case-studies/federuzzi-development-ingle-farm.jpg",
    cardImage: "/images/case-studies/federuzzi-development-ingle-farm-card.jpg",
    shortQuote:
      "Two double-garage homes squeezed onto an odd-shaped subdivision block in Ingle Farm, with the equity earmarked to fund the next development.",
    publishedAt: "2026-06-13",
    updatedAt: "2026-06-13",
    hideCta: false,
    client: {
      name: "Federuzzi Development",
      location: "Ingle Farm, SA 5098",
      suburb: "Ingle Farm",
      councilArea: "City of Salisbury",
      familySituation: "Seasoned investor running a sit-and-forget subdivision project",
    },
    project: {
      type: "Subdivision · two-home development",
      region: "Northern Adelaide",
      builderPartner: "Undisclosed builder (turnkey delivery)",
      blockType: "Odd-shaped subdivision block, widest at the frontage",
      sizeSqm: null,
      bedrooms: 3,
      bathrooms: 2,
      garage: "Double garage (per home)",
      startedAt: null,
      completedAt: null,
      durationMonths: null,
      timeToCommence: null,
    },
    challenge:
      "Federuzzi Development came to BuildPilot with a subdivision project on an awkward block in Ingle Farm. The brief was to fit two homes on the site, each with a double garage. The catch: the widest point of the block was at the front and it narrowed quickly behind that. Without careful planning, getting two double-garage homes onto the site was either going to compromise the design or compromise the saleability. As a seasoned investor running this as a sit-and-forget project, the client also wanted the build delivered turnkey so he could free up the equity within twelve months and roll it straight into the next development.",
    sectionLabels: {
      challenge: "The Situation",
      process: "Approach",
      solution: "Outcome",
      highlights: "Key Takeaways",
    },
    challengeSubpoints: [
      "Odd-shaped block, widest at the front, narrowing toward the rear",
      "Two double-garage homes had to fit without making either feel cramped",
      "Investor brief - needed to be delivered turnkey with minimal day-to-day involvement",
      "Equity needed to be released within twelve months to fund the next project",
    ],
    process: [
      {
        title: "One-on-one site strategy",
        body:
          "The project was delivered through BuildPilot's one-on-one service. Sessions focused on testing layouts against the actual block dimensions rather than forcing a standard subdivision template onto a non-standard site.",
      },
      {
        title: "Frontage stretched, designs widened at the front",
        body:
          "Because the widest part of the block sat at the front, the homes were brought closer to the front boundary and stretched wider across the frontage. That bought enough room to fit a double garage on each lot without compromising bedroom or living layouts further back.",
      },
      {
        title: "Turnkey delivery via an undisclosed builder",
        body:
          "An experienced builder was selected to deliver both homes turnkey. As an investment project, the priority was a hands-off process with predictable timelines so the client could stay focused on the next development rather than weekly site decisions.",
      },
      {
        title: "Open-plan layout that maximises every metre",
        body:
          "Each home was designed as a three-bedroom, two-bathroom layout with open-plan living and an alfresco. The internal flow was tuned to make every metre count on a tight subdivision site - easy to live in, easy to rent, easy to sell.",
      },
    ],
    solution:
      "Two double-garage, three-bedroom, two-bathroom homes were successfully placed on the Ingle Farm subdivision block, each with open-plan living and an alfresco. The site was fully maximised by leveraging the wider frontage, the turnkey delivery freed the investor from day-to-day involvement, and the project was structured so the equity could be unlocked within twelve months to fund the next development.",
    highlights: [
      "Two double-garage homes squeezed onto an odd-shaped subdivision block",
      "Frontage stretched and homes widened at the front to make the garages fit",
      "Three-bedroom, two-bathroom open-plan layout with alfresco on each home",
      "Turnkey delivery - sit-and-forget for the investor",
      "Equity targeted to roll into the next development within twelve months",
      "Delivered through BuildPilot's one-on-one service",
    ],
    keyTakeaways:
      "This project shows that an odd-shaped subdivision block is not a dealbreaker for getting two solid homes on the site. By reading the block, pushing the frontage forward and stretching it wider where the geometry allowed, both homes ended up with the double garage and layout the investor needed - without the design compromises that usually plague tight subdivisions.",
    beforeAfter: null,
    gallery: [
      "/images/case-studies/federuzzi-development-ingle-farm.jpg",
      "/images/case-studies/federuzzi-development-ingle-farm-alfresco.jpg",
      "/images/case-studies/federuzzi-development-ingle-farm-living.jpg",
      "/images/case-studies/federuzzi-development-ingle-farm-kitchen.jpg",
      "/images/case-studies/federuzzi-development-ingle-farm-kitchen-island.jpg",
      "/images/case-studies/federuzzi-development-ingle-farm-bathroom.jpg",
      "/images/case-studies/federuzzi-development-ingle-farm-hallway.jpg",
      "/images/case-studies/federuzzi-development-ingle-farm-bedroom.jpg",
    ],
    pullQuote: null,
    relatedGuides: [
      { title: "Property Development Adelaide", href: "/property-development" },
      { title: "Building in the City of Salisbury", href: "/building-in/salisbury" },
      { title: "Dual Occupancy in Adelaide", href: "/dual-occupancy-adelaide" },
      { title: "Home Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "Investor Home Builders Northern Adelaide", href: "/investor-home-builders-northern-adelaide" },
      { title: "Cost to Build a House in Adelaide", href: "/building-costs-adelaide-2026" },
    ],
    keywords: [
      "subdivision ingle farm",
      "two homes on one block adelaide",
      "ingle farm property development",
      "double garage subdivision adelaide",
      "city of salisbury subdivision",
      "buildpilot client federuzzi",
      "investor home build adelaide",
    ],
  },

  "c-foster-glenelg-north": {
    slug: "c-foster-glenelg-north",
    testimonialAuthor: "C. Foster",
    metaTitle: "C. Foster: Glenelg North Builder Switch Case Study | BuildPilot",
    metaDescription:
      "How BuildPilot helped C. Foster in Glenelg North, SA switch from a rigid major builder to a medium-sized builder offering flexibility, refined design and customised selections.",
    heroImage: "/images/testimonials/kgg-view.jpg",
    cardImage: "/images/testimonials/kgg-view.jpg",
    shortQuote:
      "When pre-construction stalls on selections and design, the right builder change can rescue the entire project.",
    publishedAt: "2026-02-26",
    updatedAt: "2026-02-26",
    hideCta: false,
    client: {
      name: "C. Foster",
      location: "Glenelg North, SA 5045",
      suburb: "Glenelg North",
      councilArea: "City of Holdfast Bay",
      familySituation: "Already in pre-construction with a major builder, frustrated at selections stage",
    },
    project: {
      type: "New home build (after switching builders pre-construction)",
      region: "Western Adelaide",
      builderPartner: "Medium-sized builder (replaced original major builder)",
      blockType: "Existing block",
      sizeSqm: null,
      bedrooms: null,
      bathrooms: null,
      garage: null,
      startedAt: null,
      completedAt: null,
      durationMonths: "Approximately 8",
      timeToCommence: "Approximately 10 months",
    },
    challenge:
      "C. Foster began their journey with a major builder in Glenelg North and had already progressed well into pre-construction. However, once they reached the selections stage, issues started to surface. Simple requests were not being accommodated. One example was a corner-style sink they wanted to include. They were willing to pay for the upgrade, but the builder would not allow it. There were also concerns around the overall design. The layout did not feel right, and the flow of the home was not what they had expected. The process became increasingly frustrating.",
    sectionLabels: {
      challenge: "The Situation",
      process: "Approach",
      solution: "Outcome",
      highlights: "Key Takeaways",
    },
    challengeSubpoints: [
      "Lack of flexibility from the original builder",
      "Limited ability to make design changes",
      "Frustration during the selections process",
      "Concerns that the home was not being tailored to their needs",
      "Already invested time and money into pre-construction with the original builder",
    ],
    process: [
      {
        title: "Review of the existing design",
        body:
          "The first step was to review the existing design and understand what was not working. The layout, flow and selections concerns were unpacked properly so the next builder could be matched against a clear brief rather than starting from scratch.",
      },
      {
        title: "Identifying a better builder fit",
        body:
          "The focus shifted to finding a builder who could deliver a better balance between structure and flexibility. A medium-sized builder was identified as the right fit, offering strong pricing through buying power, greater flexibility compared to larger builders, and the ability to source and incorporate selections outside standard ranges.",
      },
      {
        title: "Design refined with the new builder",
        body:
          "The new builder was open to refining the design and accommodating the changes that were previously not possible. This included improving the layout and allowing for more customised selections, including upgrades the original builder had refused.",
      },
      {
        title: "Ongoing support through to handover",
        body:
          "Some minor challenges came up along the way, including issues with neighbours during construction. Continued support meant these were managed without derailing the project, and the build moved through to handover predictably.",
      },
    ],
    solution:
      "The home was completed and ready to move into, with a design that better suited the client's needs and preferences. The switch from a rigid major builder to a medium-sized builder offering flexibility transformed the experience. Selections that had previously been refused were incorporated, the layout was improved, and the project moved forward without the friction that had defined the earlier stages.",
    highlights: [
      "Builder type matched to project: medium-sized builder",
      "Project type: new home build (after switching builders pre-construction)",
      "Pre-construction timeframe: approximately 10 months",
      "Construction duration: approximately 8 months",
      "Selections previously refused (e.g. corner sink upgrade) were incorporated",
      "Layout and flow refined before construction began",
    ],
    keyTakeaways:
      "This project shows how important builder flexibility can be, especially during the design and selections stages. Even small changes can make a big difference to how a home functions. Having a builder who is willing to adapt can completely change the outcome of a project.",
    beforeAfter: null,
    gallery: [],
    pullQuote: null,
    relatedGuides: [
      { title: "Home Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "Building a New Home in Adelaide", href: "/building-a-new-home" },
      { title: "Custom Home Builders Adelaide", href: "/custom-home-builders-adelaide" },
      { title: "Single Storey Builders in Western Adelaide", href: "/single-storey-builders-western-adelaide" },
      { title: "Editorial Process & Standards", href: "/about/editorial-process" },
    ],
    keywords: [
      "switching home builders adelaide",
      "glenelg north home builder",
      "medium sized home builder adelaide",
      "selections process new home build",
      "builder flexibility design changes adelaide",
      "buildpilot client glenelg north",
    ],
  },

  "tanya-chris-christies-beach": {
    slug: "tanya-chris-christies-beach",
    testimonialAuthor: "Tanya & Chris",
    metaTitle: "Tanya & Chris: Christies Beach KDR with Custom Garage & Shed | BuildPilot",
    metaDescription:
      "How BuildPilot helped Tanya and Chris in Christies Beach, SA design a knockdown rebuild around a Holden HQ Monaro restoration, with rear vehicle access and a flexible builder.",
    heroImage: "/images/heroes/tanya-chris-site-plan.webp",
    cardImage: "/images/heroes/tanya-chris-site-plan.webp",
    shortQuote:
      "A knockdown rebuild in Christies Beach designed around a slope, a rear easement and a Holden HQ Monaro shed.",
    publishedAt: "2026-02-26",
    updatedAt: "2026-02-26",
    hideCta: false,
    client: {
      name: "Tanya & Chris",
      location: "Christies Beach, SA 5165",
      suburb: "Christies Beach",
      councilArea: "City of Onkaparinga",
      familySituation: "Couple planning a knockdown rebuild around a long-term car restoration project",
    },
    project: {
      type: "Knockdown rebuild with custom garage and shed integration",
      region: "Southern Adelaide",
      builderPartner: "Two builders: home construction + external works and shed",
      blockType: "Sloped block with large rear easement",
      sizeSqm: null,
      bedrooms: null,
      bathrooms: null,
      garage: null,
      startedAt: null,
      completedAt: null,
      durationMonths: "Approximately 8 months (home build), plus external works and shed",
      timeToCommence: "Approximately 12 months",
    },
    challenge:
      "Tanya and Chris were planning a knockdown rebuild in Christies Beach on a block that had a few unique characteristics. The site had a slight slope, which initially raised some questions, and a large easement at the rear that they had not fully accounted for. At the same time, Chris had a clear priority. He had been restoring his Holden HQ Monaro for years, and the garage and shed setup was a key part of the project. This was not something that could be treated as an afterthought. The home needed to be designed around it.",
    sectionLabels: {
      challenge: "The Situation",
      process: "Approach",
      solution: "Outcome",
      highlights: "Key Takeaways",
    },
    challengeSubpoints: [
      "Designing a home that worked with the slope and site conditions",
      "Accounting for a large rear easement that limited usable space",
      "Creating a layout that allowed vehicle access from the front to the rear",
      "Planning for a large shed that suited Chris's needs",
      "Finding builders flexible enough to allow some client involvement",
      "Chris, a cabinetmaker, wanted to complete parts of the home himself",
    ],
    process: [
      {
        title: "Detailed block review",
        body:
          "The process began with reviewing the block in detail. The slope was assessed and confirmed to be manageable, but the rear easement required careful planning. The layout of the home was adjusted to ensure the available space was used properly while still allowing access through to the backyard.",
      },
      {
        title: "Home and shed designed together",
        body:
          "Design focused on how the home and shed would work together. Vehicle access from the street through to the rear, positioning of the home to allow for driveway flow, and ensuring the shed did not feel disconnected from the overall layout were all factored in from the start.",
      },
      {
        title: "Two-builder structure",
        body:
          "Two builders were engaged for different stages of the project. The first builder was selected to construct the home. The second builder was brought in to handle external works including the driveway, perimeter concrete, stormwater, and the shed construction.",
      },
      {
        title: "Cabinetry left to the client",
        body:
          "A builder was selected who was comfortable allowing Chris to complete his own cabinetry, giving him the level of control he was after. Throughout the process, decisions were made carefully and the clients took their time to ensure everything was right before moving forward.",
      },
    ],
    solution:
      "A knockdown rebuild structured around lifestyle priorities. The home and shed were planned as one cohesive outcome rather than separate elements, with vehicle access and rear flow built into the layout from day one. The two-builder structure played to each trade's strengths, and the chosen home builder gave Chris the flexibility to complete his own cabinetry.",
    highlights: [
      "Builder structure: home builder for the build, dedicated builder for external works and shed",
      "Pre-construction timeframe: approximately 12 months",
      "Construction duration (home): approximately 8 months, followed by external works and shed",
      "Slope and rear easement assessed and designed around early",
      "Front-to-rear vehicle access planned into the home layout",
      "Cabinetmaker client retained scope to complete his own cabinetry",
    ],
    keyTakeaways:
      "This project highlights the importance of designing around lifestyle priorities. By considering the garage and access requirements from the beginning, the home and shed were able to be planned as one cohesive outcome rather than separate elements. It also shows the value of a flexible approach, where the right builder can accommodate client involvement without compromising the overall result.",
    beforeAfter: null,
    gallery: [],
    pullQuote: null,
    relatedGuides: [
      { title: "Knockdown Rebuild Adelaide", href: "/knockdown-rebuild-adelaide" },
      { title: "Building in the City of Onkaparinga", href: "/building-in/onkaparinga" },
      { title: "Choosing Land in Adelaide", href: "/choosing-land" },
      { title: "Home Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "Single Storey Builders in Southern Adelaide", href: "/single-storey-builders-southern-adelaide" },
    ],
    keywords: [
      "knockdown rebuild christies beach",
      "christies beach home builder",
      "city of onkaparinga kdr",
      "home with large shed adelaide",
      "rear vehicle access home design adelaide",
      "buildpilot client christies beach",
    ],
  },

  "anyek-residence-south-australia": {
    slug: "anyek-residence-south-australia",
    testimonialAuthor: "Anyek Residence",
    metaTitle: "Anyek Residence: Builder Liquidation Recovery Case Study | BuildPilot",
    metaDescription:
      "How BuildPilot supported the Anyek family in Adelaide through a builder liquidation mid-build, securing a second builder, insurer liaison and a strong final outcome.",
    heroImage: null,
    cardImage: null,
    shortQuote:
      "Support that continued through every stage of the build, including a builder liquidation mid-construction.",
    publishedAt: "2026-02-26",
    updatedAt: "2026-02-26",
    hideCta: false,
    client: {
      name: "Anyek Residence",
      location: "Adelaide, SA",
      suburb: "Adelaide",
      councilArea: "South Australia",
      familySituation: "Family unsure which builder was right for their project",
    },
    project: {
      type: "New home build with mid-build builder transition",
      region: "Adelaide",
      builderPartner: "Initial: boutique custom builder. Completion: medium-sized builder.",
      blockType: "Existing block",
      sizeSqm: null,
      bedrooms: null,
      bathrooms: null,
      garage: null,
      startedAt: null,
      completedAt: null,
      durationMonths: null,
      timeToCommence: "Approximately 8 to 9 months",
    },
    challenge:
      "The Anyek family came to BuildPilot early in their journey, unsure which builder was right for their project. They were initially working with a boutique builder who specialised in custom homes, but there were early concerns around both design and pricing. The layout was not making the most of the block, and the numbers they were receiving did not give them confidence. They felt like they were not getting the right advice and needed a clearer direction.",
    sectionLabels: {
      challenge: "The Situation",
      process: "Approach",
      solution: "Outcome",
      highlights: "Key Takeaways",
    },
    challengeSubpoints: [
      "The design was not maximising the potential of the site",
      "Pricing lacked transparency and confidence",
      "The overall approach did not align with what they were trying to achieve",
      "Part way through construction, the original builder went into liquidation",
      "Immediate uncertainty around insurance, completion percentage, and who could step in",
    ],
    process: [
      {
        title: "Early design and pricing refinement",
        body:
          "Initially, we worked alongside the original builder to improve and refine the design, ensure the layout made better use of the block, and bring more clarity to pricing and inclusions.",
      },
      {
        title: "Insurer liaison after liquidation",
        body:
          "When the builder went into liquidation, the focus shifted quickly. We assisted with liaising with the insurer to understand the claims and completion process, so the clients knew exactly where they stood.",
      },
      {
        title: "Stage review and pricing the remaining works",
        body:
          "The stage of construction was reviewed and the remaining works identified. Updated pricing was organised so the outstanding scope was transparent before any new builder was engaged.",
      },
      {
        title: "Second builder secured to complete the project",
        body:
          "Our builder network was leveraged to secure a second builder willing to step in. A medium-sized builder was selected with the capacity and flexibility to take over at a late stage and carry the project through to completion.",
      },
    ],
    solution:
      "Despite the disruption of a mid-build liquidation, the home was completed with a significantly improved design outcome and a clear path forward after the builder transition. Continuous involvement from before construction began through to handover meant the clients always had structured guidance during what is one of the most stressful situations a homeowner can face.",
    highlights: [
      "Builder journey: boutique custom builder initially, medium-sized builder for completion",
      "Pre-construction timeframe: approximately 8 to 9 months",
      "Design and pricing refined before construction began",
      "Insurer claims and completion process navigated after liquidation",
      "Second builder secured through existing network to finish the project",
      "Final design outcome significantly improved on the original direction",
    ],
    keyTakeaways:
      "This project shows how important it is to have support not just at the beginning, but all the way through. When things go to plan, it helps refine decisions. When things do not, it becomes critical in navigating the situation and keeping the project moving.",
    beforeAfter: null,
    gallery: [],
    pullQuote:
      "We were unsure which builder was right for our project. BuildPilot assessed our situation and matched us with a fantastic builder who delivered exactly what we wanted. We could not have done it without their help.",
    relatedGuides: [
      { title: "Home Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "Builder Selection Guide", href: "/home-builders-adelaide" },
      { title: "Building a New Home in Adelaide", href: "/building-a-new-home" },
      { title: "Custom Home Builders Adelaide", href: "/custom-home-builders-adelaide" },
      { title: "Editorial Process & Standards", href: "/about/editorial-process" },
    ],
    keywords: [
      "builder went into liquidation adelaide",
      "what to do if your builder goes broke",
      "home indemnity insurance claim sa",
      "second builder to finish home adelaide",
      "buildpilot client anyek",
      "home builder liquidation case study south australia",
    ],
  },

  "ochieng-residence-morphett-vale": {
    slug: "ochieng-residence-morphett-vale",
    testimonialAuthor: "Ochieng Residence",
    metaTitle: "Ochieng Residence: Morphett Vale First Home Build Case Study | BuildPilot",
    metaDescription:
      "How BuildPilot helped a first home buyer in Morphett Vale, SA work through land selection and a budget-friendly custom design with a major builder on a smaller block.",
    heroImage: "/images/case-studies/ochieng-residence-morphett-vale.jpg",
    cardImage: "/images/case-studies/ochieng-residence-morphett-vale-card.jpg",
    shortQuote:
      "A first home buyer guided from land purchase through to a budget-friendly custom design in Morphett Vale.",
    publishedAt: "2026-02-26",
    updatedAt: "2026-02-26",
    hideCta: false,
    client: {
      name: "Ochieng Residence",
      location: "Morphett Vale, SA 5162",
      suburb: "Morphett Vale",
      councilArea: "City of Onkaparinga",
      familySituation: "First home buyer, returning client family",
    },
    project: {
      type: "New home build",
      region: "Southern Adelaide",
      builderPartner: "Major builder with custom design approval",
      blockType: "Smaller block, vacant land",
      sizeSqm: null,
      bedrooms: null,
      bathrooms: null,
      garage: null,
      startedAt: null,
      completedAt: null,
      durationMonths: "Approximately 8",
      timeToCommence: "Approximately 12 months",
    },
    challenge:
      "The Ochieng project began after a previous family connection. We had helped her mum with a home, and she reached out when she decided to start her own journey as a first home buyer. She was looking to build in Morphett Vale, but had not yet secured a block of land. Like many first home buyers, she was unsure about the land purchasing process and did not feel confident making that decision on her own.",
    sectionLabels: {
      challenge: "The Situation",
      process: "Approach",
      solution: "Outcome",
      highlights: "Key Takeaways",
    },
    challengeSubpoints: [
      "Finding the right block of land",
      "Understanding how the land purchase process actually works",
      "Whether the chosen site was suitable for what she wanted to build",
      "How the size of the block would impact the design",
      "How to make everything work within a tighter budget",
    ],
    process: [
      {
        title: "Land guidance and agent introduction",
        body:
          "The process started with land. She was connected with a land agent who could walk her through the purchase process and explain what to expect. This gave her the confidence to move forward without feeling rushed into a decision.",
      },
      {
        title: "On-site assessment before commitment",
        body:
          "Before she committed to the block, the site was visited in person to assess it and provide feedback on any potential issues. The block was suitable, so she proceeded with the purchase.",
      },
      {
        title: "Major builder with custom design approval",
        body:
          "A major builder was selected because their pricing structure suited her budget. The block size, however, required a more thoughtful design approach, so approval was obtained from the builder to move beyond standard plans and develop a custom design that suited the site.",
      },
      {
        title: "Considered layout for a smaller block",
        body:
          "The layout was carefully considered to maximise the available space. This allowed her to achieve a more tailored outcome on a smaller block without moving outside her budget.",
      },
    ],
    solution:
      "A well-designed home that made strong use of a smaller block in Morphett Vale, while staying within budget. The longer pre-construction phase was largely due to finance delays, but the project moved forward successfully once approvals were in place. The combination of a major builder for cost certainty and custom design approval for site fit gave her the best of both pathways.",
    highlights: [
      "Builder type matched to project: major builder with custom design approval",
      "Project type: new home build",
      "Pre-construction timeframe: approximately 12 months",
      "Construction duration: approximately 8 months",
      "Land guidance provided before the block was committed to",
      "Custom design approval secured to suit a smaller block within budget",
    ],
    keyTakeaways:
      "This project highlights how important it is to get the land decision right, especially for first home buyers. With the right support early on, it becomes much easier to move through both the land purchase and building process with confidence, even when working within tighter constraints.",
    beforeAfter: null,
    gallery: [],
    pullQuote: null,
    relatedGuides: [
      { title: "Building in the City of Onkaparinga", href: "/building-in/onkaparinga" },
      { title: "First Home Buyer Guide", href: "/first-home-buyer-adelaide" },
      { title: "House and Land Packages Adelaide", href: "/house-and-land-packages-adelaide" },
      { title: "Choosing Land in Adelaide", href: "/choosing-land" },
      { title: "Home Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "Single Storey Builders in Southern Adelaide", href: "/single-storey-builders-southern-adelaide" },
    ],
    keywords: [
      "morphett vale home builder",
      "first home buyer morphett vale",
      "city of onkaparinga new build",
      "small block home design adelaide",
      "southern adelaide first home build",
      "buildpilot client morphett vale",
    ],
  },

  "family-extension-gilles-plains": {
    slug: "family-extension-gilles-plains",
    testimonialAuthor: "Family Extension Project",
    metaTitle: "Family Extension Project: Gilles Plains Renovation Case Study | BuildPilot",
    metaDescription:
      "How BuildPilot helped a multi-generational family in Gilles Plains, SA plan a staged renovation and extension on a tight budget with no prior building experience.",
    heroImage: null,
    cardImage: null,
    shortQuote:
      "A staged renovation and extension in Gilles Plains, structured so the family could deliver parts of the home themselves.",
    publishedAt: "2026-02-26",
    updatedAt: "2026-02-26",
    hideCta: false,
    client: {
      name: "Family Extension Project",
      location: "Gilles Plains, SA 5086",
      suburb: "Gilles Plains",
      councilArea: "City of Port Adelaide Enfield",
      familySituation: "Multi-generational family, first-time builders",
    },
    project: {
      type: "Renovation and extension (design and build)",
      region: "Northern Adelaide",
      builderPartner: "Small custom builder",
      blockType: "Existing home, staged extension",
      sizeSqm: null,
      bedrooms: null,
      bathrooms: null,
      garage: null,
      startedAt: null,
      completedAt: null,
      durationMonths: "Approximately 12 (total)",
      timeToCommence: "Approximately 6 months pre-construction",
    },
    challenge:
      "This project involved a multi-generational family looking to renovate and extend their home in Gilles Plains. They had never been through the building process before and were finding it difficult to even get started. They had spent months trying to find a builder but were not getting anywhere. On top of that, there were personal circumstances that made this project more important than usual. They were not just building for practical reasons, but to create something they could look forward to as a family. They needed clarity, guidance, and someone to help them navigate the process from the ground up.",
    sectionLabels: {
      challenge: "The Situation",
      process: "Approach",
      solution: "Outcome",
      highlights: "Key Takeaways",
    },
    challengeSubpoints: [
      "No prior experience with building or renovations",
      "Difficulty finding a suitable builder",
      "Limited budget that required careful planning",
      "A need to stage the project over time",
      "Desire to maintain control over certain costs and finishes",
    ],
    process: [
      {
        title: "Design and build education",
        body:
          "The process started by helping the family understand how a design and build pathway would work for their situation. This was their first build, so groundwork on terminology, sequencing and what to expect was essential before any builder conversations began.",
      },
      {
        title: "Small custom builder match",
        body:
          "A suitable small custom builder was identified who could accommodate a fully custom renovation and extension, while remaining flexible with how the project would be delivered. Smaller builders often have more flexibility on staging and finishes than volume builders.",
      },
      {
        title: "Builder liaison and design support",
        body:
          "Ongoing liaison with the builder, plus help with design discussions and layout direction. The family had ideas about flow and use of space, and that input was structured into the design conversations rather than left to the builder alone.",
      },
      {
        title: "Pricing review and project staging",
        body:
          "Pricing was reviewed and validated as it came through. The project was structured so it could be delivered in stages, allowing the family to complete parts of the home themselves after handover, including the kitchen, tiling, and selected finishes, to manage costs.",
      },
    ],
    solution:
      "A staged renovation and extension that gave the family a functional and flexible home, while allowing them to complete selected finishes in their own time. The hybrid build-yourself-the-rest approach kept the project affordable without compromising on the structural and big-ticket items where it mattered most.",
    highlights: [
      "Builder type matched to project: small custom builder",
      "Project type: renovation and extension via design and build",
      "Pre-construction timeframe: approximately 6 months",
      "Total project timeframe: approximately 12 months",
      "Stages structured so the family could finish kitchen, tiling and selected finishes themselves",
      "Project delivered to budget with clear, manageable decisions throughout",
    ],
    keyTakeaways:
      "This project shows that even without prior experience, and with a tighter budget, a well-structured approach can make the process manageable. Breaking the project into stages and focusing on what mattered most allowed the family to move forward with clarity and confidence in Gilles Plains.",
    beforeAfter: null,
    gallery: [],
    pullQuote: null,
    relatedGuides: [
      { title: "Building in Port Adelaide Enfield", href: "/building-in/port-adelaide-enfield" },
      { title: "Home Extensions in Adelaide", href: "/home-extensions-adelaide" },
      { title: "Home Builders Adelaide", href: "/home-builders-adelaide" },
      { title: "Which Builder Should I Choose?", href: "/which-builder-should-i-choose" },
      { title: "Best Home Builders Under $400K in Northern Adelaide", href: "/home-builders-under-400k-northern-adelaide" },
      { title: "Family Home Builders in Northern Adelaide", href: "/family-home-builders-northern-adelaide" },
    ],
    keywords: [
      "home extension gilles plains",
      "renovation builder gilles plains",
      "design and build extension adelaide",
      "small custom builder adelaide renovation",
      "staged renovation extension adelaide",
      "buildpilot client gilles plains",
    ],
  },
};

export const getCaseStudy = (slug) => caseStudyData[slug] || null;

export const getCaseStudyByAuthor = (authorName) => {
  const entries = Object.values(caseStudyData);
  return entries.find((c) => c.testimonialAuthor === authorName && !c.isPlaceholder) || null;
};

export const getAllCaseStudies = () =>
  Object.values(caseStudyData)
    .filter((c) => !c.isPlaceholder)
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

export const getAllCaseStudySlugs = () =>
  Object.keys(caseStudyData).filter((slug) => !caseStudyData[slug].isPlaceholder);

// --- SEO-focused helpers ---------------------------------------------------

// Score similarity between two case studies for "Related case studies" surfacing.
// Higher = more relevant. Same project category > same region > same council > shared keywords.
const projectCategory = (s) => {
  const t = (s?.project?.type || '').toLowerCase();
  if (t.includes('knockdown') || t.includes('kdr')) return 'kdr';
  if (t.includes('renovation') || t.includes('extension')) return 'renovation';
  if (t.includes('subdivision')) return 'subdivision';
  return 'new-build';
};

export const getRelatedCaseStudies = (slug, limit = 3) => {
  const target = caseStudyData[slug];
  const all = getAllCaseStudies().filter((s) => s.slug !== slug);
  if (!target) return all.slice(0, limit);
  const targetCat = projectCategory(target);
  const targetRegion = target.project?.region || '';
  const targetCouncil = target.client?.councilArea || '';
  const targetKeywords = new Set((target.keywords || []).map((k) => k.toLowerCase()));

  const scored = all.map((s) => {
    let score = 0;
    if (projectCategory(s) === targetCat) score += 5;
    if (s.project?.region === targetRegion && targetRegion) score += 3;
    if (s.client?.councilArea === targetCouncil && targetCouncil) score += 2;
    const overlap = (s.keywords || []).filter((k) => targetKeywords.has(k.toLowerCase())).length;
    score += overlap;
    return { study: s, score };
  });

  return scored
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return new Date(b.study.publishedAt) - new Date(a.study.publishedAt);
    })
    .slice(0, limit)
    .map((x) => x.study);
};

// Topic-based filter for embedding case studies inside guides/landing pages.
// topics: { suburb?: string, councilArea?: string, region?: string,
//           projectType?: 'kdr'|'new-build'|'renovation'|'subdivision'|'first-home',
//           keywords?: string[] }
export const getCaseStudiesForTopic = (topics = {}, limit = 3) => {
  const all = getAllCaseStudies();
  const wantKeywords = new Set((topics.keywords || []).map((k) => k.toLowerCase()));

  const scored = all.map((s) => {
    let score = 0;
    if (topics.suburb && s.client?.suburb?.toLowerCase() === topics.suburb.toLowerCase()) score += 10;
    if (topics.councilArea && s.client?.councilArea?.toLowerCase() === topics.councilArea.toLowerCase()) score += 6;
    if (topics.region && s.project?.region?.toLowerCase() === topics.region.toLowerCase()) score += 3;
    if (topics.projectType) {
      if (topics.projectType === 'first-home') {
        const fam = (s.client?.familySituation || '').toLowerCase();
        if (fam.includes('first home') || fam.includes('first-time')) score += 5;
      } else if (projectCategory(s) === topics.projectType) {
        score += 5;
      }
    }
    const overlap = (s.keywords || []).filter((k) => wantKeywords.has(k.toLowerCase())).length;
    score += overlap;
    return { study: s, score };
  });

  return scored
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.study);
};
